import { ReactNode, Suspense, lazy } from 'react'
import { Outlet } from 'react-router'
import { Navbar } from '@/ui/shared/layout'
import { CustomCommandPalette } from '@/ui/components/command-palette'
import { ChatProvider, AIChat } from '@/ui/components/ai-chat'
import { Announcement } from '@/ui/components/announcement'
import {
  useEcommerceCopies,
  useEcommerceScope,
  usePageVisit,
  useSettings
} from '@/hooks'
import ErrorBoundary from '@/ErrorBoundary'
import DialogProvider from '@/ui/components/dialog/dialog-provider'
import { useAuth } from '@/hooks'
import { ReverLoading, ReverLoadingModal } from '@itsrever/design-system'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import ErrorNotFound from '@/ui/pages/404.page'
import { SAVE_BAR_SLOT_ID } from '@/ui/shared/save-bar'

const ErrorPage = lazy(() => import('@/ui/pages/error.page'))

const Layout = () => {
  const { isLoadingMe } = useAuth()

  useEcommerceScope()
  usePageVisit()
  useSettings()

  const { loadingEcommerceCopies } = useEcommerceCopies()

  if (isLoadingMe || loadingEcommerceCopies)
    return <ReverLoadingModal loading />

  return (
    <ChatProvider>
      <div className="mx-auto my-0 flex w-full">
        <Navbar />
        <CustomCommandPalette />
        <PageFrame>
          <DialogProvider>
            <ErrorBoundary fallback={<ErrorPage />}>
              <Suspense fallback={<ReverLoading />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </DialogProvider>
        </PageFrame>
        {/* Last in the row: on wide viewports the assistant docks here as a
            column and the page shrinks beside it instead of being covered. */}
        <AIChat />
        <Announcement />
      </div>
    </ChatProvider>
  )
}

// The rounded page beside the navbar. Its scroll container is the one every page scrolls in.
const PageFrame = ({ children }: { children: ReactNode }) => {
  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  const isReviewer = role === Roles.Reviewer
  const isAnalyticsOnly = role === Roles.AnalyticsOnly
  const isReturnsContent = location.pathname.includes('/returns')
  const isAnalyticsContent = location.pathname.includes('/analytics')
  const isHomeContent = location.pathname === '/'
  const showNotFound =
    (isReviewer && !isReturnsContent && !isHomeContent) ||
    (isAnalyticsOnly && !isAnalyticsContent)

  return (
    <div
      className="flex h-screen w-full overflow-hidden bg-shell p-2 pl-0 transition-all"
      id="page-content"
    >
      <div className="relative h-full flex-1 overflow-hidden rounded-[20px] bg-neutral-100">
        <div
          id={SAVE_BAR_SLOT_ID}
          className="pointer-events-none absolute inset-x-0 bottom-5 z-10 flex justify-center"
        />
        {showNotFound ? (
          <ErrorNotFound />
        ) : (
          // overscroll-none: without it macOS rubber-bands the page at either end and the sticky
          // tabs come along, leaving a blank strip above them. The padding while a SaveBar floats
          // in the slot keeps it off the end of the page.
          <div className="h-full overflow-auto overscroll-none [#save-bar-slot:not(:empty)~&]:pb-16">
            {children}
          </div>
        )}
      </div>
    </div>
  )
}

export default Layout
